class DisclosureComponent extends HTMLElement {
  #triggers = [];
  #content = null;
  #method = 'until-found';

  #shouldFocusWithin = false;
  #shouldReturnFocus = false;
  #returnFocusTo = null;

  #openedBy = null;

  #open = false;
  get open() {
    return this.#open;
  }
  set open(open) {
    if (open === this.#open) return;
    if (typeof open !== 'boolean') {
      console.error(`Disclosure component: Invalid state value: ${open}`, 'Expected a boolean.');
      return;
    }
    this.#open = open;
    this.#updateDOM();
  }

  #IS_COMMANDFOR_SUPPORTED = false;

  constructor() {
    super();
    this.#IS_COMMANDFOR_SUPPORTED = (() => {
      try {
        const element = document.createElement('button');
        return 'commandForElement' in element;
      } catch {
        return false;
      }
    })();
  }

  connectedCallback() {
    this.#content = this.querySelector('div[data-disclosure-content]');

    if (!this.#content) {
      console.error(
        'Disclosure component requires a child <div> with attribute hidden="until-found" to function properly.'
      );
      return;
    }
    if (this.#content.hasAttribute('hidden')) {
      this.open = false;
      this.#method = this.#content.getAttribute('hidden') === 'until-found' ? 'until-found' : '';
    } else {
      this.open = true;
    }

    this.#shouldFocusWithin = this.hasAttribute('focus-within');
    this.#shouldReturnFocus = this.hasAttribute('return-focus');
    this.#returnFocusTo = this.getAttribute('return-focus')
      ? document.querySelector(this.getAttribute('return-focus'))
      : null;

    this.#handleCommandBound = this.#handleCommand.bind(this);
    this.addEventListener('command', this.#handleCommandBound);

    this.#triggers = document.querySelectorAll(`[data-disclosure-trigger][aria-controls="${this.id}"]`);
    this.#handleTriggerClickBound = this.#handleTriggerClick.bind(this);
    this.#triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.#handleTriggerClickBound);
    });

    this.#onBeforeMatchBound = this.#onBeforeMatch.bind(this);
    this.#content.addEventListener('beforematch', this.#onBeforeMatchBound);
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.#handleCommandBound);

    this.#triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.#handleTriggerClickBound);
    });
    this.#triggers = [];

    this.#content.removeEventListener('beforematch', this.#onBeforeMatchBound);
    this.#content = null;
  }

  show() {
    this.#openedBy = null;
    const resetScrollElements = this.querySelectorAll('[data-disclosure-reset-scroll]');
    [this, ...resetScrollElements].forEach((scrollElement) => {
      scrollElement.scrollTo({ top: 0, behavior: 'instant' });
    });
    this.open = true;
    this.dispatchEvent(
      new CustomEvent('disclosure-component:show', {
        bubbles: false,
      })
    );
  }

  hide({ returnFocus = true } = {}) {
    if (!returnFocus) {
      this.#openedBy = null;
    }
    this.open = false;
    this.dispatchEvent(
      new CustomEvent('disclosure-component:hide', {
        bubbles: false,
      })
    );
  }

  toggle() {
    if (this.open) {
      this.hide();
    } else {
      this.show();
    }
  }

  #reducer(command, trigger) {
    const wasOpen = this.open;

    switch (command) {
      case '--show':
        this.show();
        this.#openedBy = trigger;
        break;
      case '--hide':
        this.hide();
        break;
      case '--toggle':
        this.toggle();
        if (!wasOpen) {
          this.#openedBy = trigger;
        }
        break;
    }
  }

  #handleCommandBound = null;
  #handleCommand(event) {
    if (event.defaultPrevented) return;

    this.#reducer(event.command, event.source);
  }

  #handleTriggerClickBound = null;
  #handleTriggerClick(event) {
    if (event.defaultPrevented) return;

    const trigger = event.currentTarget;

    const command = trigger.getAttribute('command');
    let commandFor = trigger.getAttribute('commandfor');

    if (command && commandFor && this.#IS_COMMANDFOR_SUPPORTED) {
      // Handled by `#handleCommand`
      return;
    }

    // Fallback for browsers that do not support `commandForElement` or
    // triggers without `command` attribute

    if (!commandFor) {
      commandFor = trigger.getAttribute('aria-controls');
    }

    if (commandFor !== this.id) return;

    this.#reducer(command, trigger);
  }

  #onBeforeMatchBound = null;
  #onBeforeMatch() {
    this.show();
  }

  #updateDOM() {
    if (this.#open) {
      this.#content.removeAttribute('hidden');
      this.setAttribute('data-open', 'true');
      this.#triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'true');
      });

      if (this.#shouldFocusWithin) {
        this.#focusWithin();
      }
    }

    if (!this.#open) {
      this.#content.setAttribute('hidden', this.#method);
      this.removeAttribute('data-open');
      this.#triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
      if (this.#shouldReturnFocus) {
        this.#returnFocus();
      }
    }
  }

  #focusWithin() {
    const firstFocusable = window.getFocusableElements(this.#content)?.[0];
    const focusOnContainer = () => {
      this.#content.setAttribute('tabindex', '-1');
      this.#content.focus({ preventScroll: true });
      this.#content.removeAttribute('tabindex');
    };

    if (!firstFocusable) {
      focusOnContainer();
      return;
    }

    if (firstFocusable.checkVisibility()) {
      firstFocusable.focus({ preventScroll: true });
      return;
    }

    setTimeout(() => {
      if (!this.#content || this.contains(document.activeElement)) return;

      if (firstFocusable && firstFocusable.checkVisibility()) {
        firstFocusable.focus({ preventScroll: true });
      } else {
        focusContainer();
      }
    }, 100);
  }

  #returnFocus() {
    if (!this.#returnFocusTo && !this.#openedBy) return;
    const returnTarget = this.#returnFocusTo || this.#openedBy;

    if (returnTarget.checkVisibility()) {
      returnTarget.focus({ preventScroll: true });
      return;
    }

    setTimeout(() => {
      if (!returnTarget || !this.contains(document.activeElement)) return;
      returnTarget.focus({ preventScroll: true });
    }, 100);
  }
}
customElements.define('disclosure-component', DisclosureComponent);
